<script setup lang="ts">
import { ref, shallowRef, watch, watchEffect, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { ReportDto } from '@/types'
import type { StepperItem } from '@nuxt/ui'
import { getLocalTimeZone, now, type ZonedDateTime } from '@internationalized/date'

import ReportContactFields from '@/components/report/ReportContactFields.vue'
import ReportAttachmentsField from '@/components/report/ReportAttachmentsField.vue'
import ReportDateTimeField from '@/components/report/ReportDateTimeField.vue'
import ReportStepperForm from '@/components/report/ReportStepperForm.vue'
import ReportDraftBanner from '@/components/report/ReportDraftBanner.vue'

import {
  emergencyReportSchema,
  emergencySteps
} from '@/components/report/reportSchemas'
import { useReportSubmit } from '@/composables/useReportSubmit'
import { useReportDraft } from '@/composables/useReportDraft'

const authStore = useAuthStore()

const step = ref(1) // 1 = Emergency Confirmation, 2 = Emergency Form Wizard
const selectedFiles = ref<File[]>([])
const dateValue = shallowRef<ZonedDateTime>(now(getLocalTimeZone()))

const reportStepperItems: StepperItem[] = [
  { slot: 'contact', title: 'Kontakt', icon: 'i-lucide-user' },
  { slot: 'details', title: 'Notfalldetails', icon: 'i-lucide-file-text' },
  { slot: 'attachments', title: 'Anhänge & Absenden', icon: 'i-lucide-paperclip' }
]

const confirmed = ref(false)

function createInitialForm(): ReportDto {
  return {
    type: 'emergency',
    contactPerson: authStore.userInfo?.user.fullName || '',
    phoneNumber: '',
    email: authStore.userInfo?.user.email || '',
    service: '',
    description: '',
    businessImpact: '',
    startTime: dateValue.value ? dateValue.value.toDate().toLocaleString('de-DE') : new Date().toISOString(),
    dataLoss: false,
    securityImpact: false,
    productionOutage: false,
    venaboTicket: '',
    files: []
  }
}

const form = ref<ReportDto>(createInitialForm())

watch(
  dateValue,
  (val) => {
    if (val) {
      form.value.startTime = val.toDate().toLocaleString('de-DE')
    }
  },
  { immediate: true }
)

watchEffect(() => {
  if (authStore.userInfo?.user.fullName) {
    form.value.contactPerson = authStore.userInfo.user.fullName
  }
  if (authStore.userInfo?.user.email) {
    form.value.email = authStore.userInfo.user.email
  }
})

const emergencyDraft = useReportDraft('emergency', form)
const { submitting, success, submit } = useReportSubmit()

onMounted(() => {
  emergencyDraft.checkDraft()
})

function nextStep() {
  if (confirmed.value) {
    emergencyDraft.checkDraft()
    step.value = 2
  }
}

async function handleSubmit() {
  try {
    const ok = await submit(form.value, selectedFiles.value, dateValue.value)
    if (ok) {
      emergencyDraft.clear()
    }
  } catch {
    // Error toast handled by useReportSubmit
  }
}
</script>

<template>
  <UPage>
    <UPageHeader
      title="ConnectCloud Notfall melden"
      description="Nutzen Sie diesen Prozess nur für kritische Vorfälle, die schwerwiegende Auswirkungen haben oder bald haben könnten."
      class="sticky top-0 z-20 border-b border-default bg-default/95 backdrop-blur"
    >
      <template #links>
        <UButton
          to="/feedback"
          color="neutral"
          icon="i-lucide-arrow-left"
          label="Zurück zum Support Hub"
          variant="ghost"
        />
      </template>
    </UPageHeader>

    <UPageBody>
      <div class="space-y-8">
        <!-- Success State -->
        <div v-if="success" class="space-y-6">
          <UAlert
            color="success"
            variant="subtle"
            title="Notfallmeldung erhalten"
            icon="i-lucide-check-circle"
          >
            <template #description>
              Wir haben Ihre ConnectCloud Notfallmeldung erhalten und prüfen den Vorfall. Der Notfallprozess wurde aktiviert. Wir werden den angegebenen Notfallkontakt kontaktieren, falls zusätzliche Informationen benötigt werden.
            </template>
          </UAlert>
          <div class="flex justify-end">
            <UButton to="/" color="neutral" variant="ghost">Zurück zum Dashboard</UButton>
          </div>
        </div>

        <div v-else>
          <!-- Step 1: Definition & Confirmation -->
          <div v-if="step === 1" class="space-y-8">
            <div class="rounded-2xl border border-error-200 bg-error-50/40 p-6 dark:border-error-900/50 dark:bg-error-950/10">
              <div class="space-y-3">
                <UBadge color="error" variant="subtle">Bitte zuerst prüfen</UBadge>
                <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Ist es wirklich ein Notfall?</h2>
                <p class="text-gray-600 dark:text-gray-300">
                  Nutzen Sie diesen Weg nur für kritische Vorfälle, die nicht bis zum nächsten regulären Werktag warten können.
                </p>
              </div>
            </div>

            <UAlert
              color="info"
              variant="subtle"
              icon="i-lucide-info"
              title="Systemstatus prüfen"
            >
              <template #description>
                Möglicherweise ist uns die Störung bereits bekannt. Bitte prüfen Sie zuerst unsere Statusseite unter <a href="https://status.mbcom.cloud" target="_blank" class="font-semibold underline">status.mbcom.cloud</a>, ob wir bereits an einer Behebung arbeiten.
              </template>
            </UAlert>

            <section class="space-y-4">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Was ist ein Notfall?</h2>
              <p class="text-gray-600 dark:text-gray-400">
                Ein ConnectCloud-Notfall ist ein Vorfall, der vernünftigerweise nicht bis zum nächsten regulären Werktag warten kann, da er schwerwiegende Auswirkungen hat oder bald haben könnte.
              </p>
              <div class="grid md:grid-cols-2 gap-4">
                <UCard :ui="{ body: 'p-6 bg-error-50/50 dark:bg-error-950/20' }">
                  <h3 class="font-bold text-error-700 dark:text-error-400 mb-2">Beispiele für Notfälle</h3>
                  <ul class="list-disc list-inside text-sm space-y-1 text-gray-600 dark:text-gray-300">
                    <li>Totalausfall des Produktionssystems</li>
                    <li>Sicherheitsvorfall / unbefugter Zugriff</li>
                    <li>Gefahr von Datenverlust oder Datenkorruption</li>
                    <li>Kritische Beeinträchtigung ohne Workaround</li>
                  </ul>
                </UCard>
                <UCard :ui="{ body: 'p-6 bg-neutral-50/50 dark:bg-neutral-900/20' }">
                  <h3 class="font-bold text-neutral-700 dark:text-neutral-400 mb-2">KEINE Notfälle</h3>
                  <ul class="list-disc list-inside text-sm space-y-1 text-gray-600 dark:text-gray-300">
                    <li>Fragen zur Konfiguration oder Nutzung</li>
                    <li>Wünsche für neue Funktionen</li>
                    <li>Kleinere Fehler mit verfügbarem Workaround</li>
                    <li>Probleme in Test- oder Staging-Umgebungen</li>
                  </ul>
                </UCard>
              </div>
            </section>

            <UAlert
              color="warning"
              variant="soft"
              icon="i-lucide-alert-triangle"
              title="Wichtiger Hinweis"
            >
              <template #description>
                Die missbräuchliche Verwendung dieses Notfallmechanismus kann gemäß der geltenden Support- oder Servicevereinbarung in Rechnung gestellt werden.
              </template>
            </UAlert>

            <div class="rounded-xl border border-neutral-200 bg-neutral-50/50 p-4 dark:border-neutral-800 dark:bg-neutral-900/30">
              <UCheckbox
                v-model="confirmed"
                label="Ich bestätige, dass dies ein echter ConnectCloud-Notfall mit kritischen Auswirkungen ist."
              />
            </div>

            <div class="flex justify-between">
              <UButton color="neutral" variant="ghost" to="/feedback">Zurück</UButton>
              <UButton
                :disabled="!confirmed"
                color="primary"
                trailing-icon="i-lucide-arrow-right"
                @click="nextStep"
              >
                Weiter zum Formular
              </UButton>
            </div>
          </div>

          <!-- Step 2: Emergency Form Wizard -->
          <div v-else-if="step === 2" class="space-y-6">
            <ReportDraftBanner
              v-if="emergencyDraft.hasDraft.value"
              @restore="emergencyDraft.restore"
              @discard="emergencyDraft.discard"
            />

            <ReportStepperForm
              :items="reportStepperItems"
              :schema="emergencyReportSchema"
              :state="form"
              :step-fields="(emergencySteps as unknown as string[][])"
              :submitting="submitting"
              submit-button-text="Notfall jetzt melden"
              submit-button-color="error"
              submit-button-icon="i-lucide-send"
              @submit="handleSubmit"
              @back="step = 1"
            >
              <template #default="{ step: activeStep }">
                <!-- Step 0: Kontakt -->
                <div v-if="activeStep === 0">
                  <ReportContactFields
                    v-model="form"
                    phone-required
                    :show-priority="false"
                  />
                </div>

                <!-- Step 1: Notfalldetails -->
                <div v-else-if="activeStep === 1" class="space-y-6">
                  <UFormField label="Kurze Fehlerbeschreibung" name="description" required class="w-full">
                    <UTextarea
                      v-model="form.description"
                      autoresize
                      placeholder="Was genau ist passiert?"
                      class="w-full"
                      :rows="4"
                    />
                  </UFormField>

                  <UFormField label="Geschäftliche Auswirkung" name="businessImpact" required class="w-full">
                    <UTextarea
                      v-model="form.businessImpact"
                      autoresize
                      placeholder="Warum ist dies kritisch für Ihr Unternehmen?"
                      class="w-full"
                      :rows="4"
                    />
                  </UFormField>

                  <div class="grid gap-6 md:grid-cols-2">
                    <ReportDateTimeField v-model="dateValue" />

                    <UFormField label="Bestehende Venabo Ticket-Nummer (optional)" name="venaboTicket" class="w-full">
                      <UInput v-model="form.venaboTicket" placeholder="V-123456" class="w-full" />
                    </UFormField>
                  </div>

                  <div class="space-y-2">
                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Vermutete Auswirkungen:</p>
                    <div class="flex flex-wrap gap-6">
                      <UCheckbox v-model="form.productionOutage" label="Produktionsausfall" />
                      <UCheckbox v-model="form.dataLoss" label="Datenverlust / Korruption" />
                      <UCheckbox v-model="form.securityImpact" label="Sicherheitsvorfall" />
                    </div>
                  </div>
                </div>

                <!-- Step 2: Anhänge & Absenden -->
                <div v-else-if="activeStep === 2">
                  <ReportAttachmentsField v-model="selectedFiles" />
                </div>
              </template>
            </ReportStepperForm>
          </div>
        </div>
      </div>
    </UPageBody>
  </UPage>
</template>
